import { type Island, type Panel } from '@rta/rta-blueprint-library';

export type IslandLocalPoint = { x: number; y: number; z: number };

/** Where the 3D ray landed, said the way the 2D menu actions expect it. */
export type HitPlacement = {
	panel: Panel | null;
	/** Inches from the object's centre, y pointing down — the 2D view's convention */
	position: { x: number; y: number };
};

/** The same centre KitchenDataManager hands the renderer, so this agrees with where the mesh sits. */
const panelCentre = (panel: Panel): { x: number; y: number; z: number } => {
	let centre: Partial<IslandLocalPoint> = panel.center;
	try {
		const translated = panel.getCornerPoints(null, { translate: true, rotate: false });
		if (translated?.center) centre = translated.center;
	} catch {
		// the renderer falls back to panel.center here too
	}
	return { x: centre.x ?? 0, y: centre.y ?? 0, z: centre.z ?? 0 };
};

/**
 * The hit point in the panel's own face frame. The 3D places a panel at the island-local
 * `(centre.x, -centre.y, centre.z)` rotated by `originAngle` about Y, with its geometry centred,
 * so undoing that rotation gives the offset along the panel's width (`x`) and across its depth
 * (`z`), and negating y returns the domain's down-positive vertical.
 */
const panelRelative = (panel: Panel, point: IslandLocalPoint): { x: number; y: number; z: number } => {
	const centre = panelCentre(panel);
	const angle = panel.getOriginAngle(true);
	const dx = point.x - centre.x;
	const dz = point.z - centre.z;
	return {
		x: (dx * Math.cos(angle)) - (dz * Math.sin(angle)),
		y: -point.y - centre.y,
		z: (dx * Math.sin(angle)) + (dz * Math.cos(angle))
	};
};

/** Slack, in inches, on a panel's half-width before a point counts as off it — joints and merged-mesh geometry aren't perfectly exact. */
const PANEL_CONTAINMENT_EPSILON_IN = 0.5;

/**
 * Which panel the ray hit. Once cutouts are generated the island's surrounds are one merged
 * mesh carrying a single panel's id, so the scene cannot answer this — containment in the
 * island's own plan (x/z) can. A point is "on" a panel when its width-axis offset falls within
 * the panel's half-width; among panels that contain it, the one it sits closest across (smallest
 * depth-axis offset) wins. A point that lands on no panel at all falls back to nearest centre.
 */
const nearestPanel = (island: Island, point: IslandLocalPoint): Panel | null => {
	let containing: Panel | null = null;
	let shortestPerpendicular = Infinity;
	let nearest: Panel | null = null;
	let shortestToCentre = Infinity;

	for (const panel of island.panels.children) {
		const { x: widthOffset, z: depthOffset } = panelRelative(panel, point);
		const onPanel = Math.abs(widthOffset) <= (panel.size.width / 2) + PANEL_CONTAINMENT_EPSILON_IN;
		if (onPanel && Math.abs(depthOffset) < shortestPerpendicular) {
			shortestPerpendicular = Math.abs(depthOffset);
			containing = panel;
		}

		const centre = panelCentre(panel);
		const distance = Math.hypot(point.x - centre.x, point.z - centre.z);
		if (distance < shortestToCentre) {
			shortestToCentre = distance;
			nearest = panel;
		}
	}

	return containing ?? nearest;
};

export type ResolveThreeDHitParams = {
	type: string;
	island: Island | null;
	/** The panel the blueprint already knows, when the hit was an appliance */
	knownPanel: Panel | null;
	islandPoint: IslandLocalPoint | null;
};

export const resolveThreeDHit = (params: ResolveThreeDHitParams): HitPlacement => {
	const { type, island, knownPanel, islandPoint } = params;

	// An overhead plan: the menus read the second axis as depth
	if (type === 'countertop') {
		return { panel: null, position: islandPoint ? { x: islandPoint.x, y: islandPoint.z } : { x: 0, y: 0 } };
	}

	// A merged surround/trim mesh carries only the first unioned panel's id, so a panel or
	// corner hit can never trust knownPanel — only an appliance's own per-mesh id is real.
	const geometricPanel = island && islandPoint ? nearestPanel(island, islandPoint) : null;
	const panel = type === 'panel' || type === 'corner' ? geometricPanel : knownPanel ?? geometricPanel;
	if (!panel || !islandPoint) return { panel, position: { x: 0, y: 0 } };

	// An appliance's own placement already decides where it sits; only a bare panel hit
	// needs the point, and feeding one in for an appliance would move it on a stray action.
	if (type !== 'panel' && type !== 'corner') return { panel, position: { x: 0, y: 0 } };

	const { x, y } = panelRelative(panel, islandPoint);
	return { panel, position: { x, y } };
};
